import React, { useState } from "react";

interface Reward {
    date: string;
    amount: number;
}

interface PayoutEntry {
    createdTime: string;
    payoutAsBtc: boolean;
    amount: number;
    valueInBtc: number;
    rewards: Reward[];
    transactionStatus: string;
    transactionId: string;
}

interface RevenuePayoutsProps {
    payouts: PayoutEntry[];
    coin: string;
}

const RevenuePayouts: React.FC<RevenuePayoutsProps> = ({ payouts, coin }) => {
    const [showTxIdIndex, setShowTxIdIndex] = useState<number | null>(null);
    const [rewardToggle, setRewardToggle] = useState<number | null>(null);

    const toggleTxIdRow = (index: number) => {
        setShowTxIdIndex((prevIndex) => (prevIndex === index ? null : index));
    };

    const toggleRewardToggle = (index: number) => {
        setRewardToggle((prevIndex) => (prevIndex === index ? null : index));
    };

    const sortedPayouts = payouts.sort((a, b) => {
        return new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime();
    });

    function toFixedNoRound(num: number, decimals: number): string {
        const factor = 10 ** decimals
        const truncated = Math.trunc(num * factor) / factor
        return truncated.toFixed(decimals)
    }

    return (
        <div className="p-0">
            <div className="overflow-hidden rounded-xl border border-blue-100 dark:border-slate-700 shadow-sm transition-colors">
                <table className="min-w-full text-blue-500 dark:text-slate-400 text-center">
                    <thead className="text-[10px] sm:text-xs text-blue-700 dark:text-slate-200 uppercase bg-blue-50 dark:bg-slate-800/50 border-b border-blue-100 dark:border-slate-700">
                        <tr>
                            <th scope="col" className="px-3 sm:px-4 md:px-6 py-3 font-semibold tracking-wider">Date</th>
                            <th scope="col" className="px-3 sm:px-4 md:px-6 py-3 font-semibold tracking-wider">Amount</th>
                            <th scope="col" className="px-3 sm:px-4 md:px-6 py-3 font-semibold tracking-wider">Status & Details</th>
                        </tr>
                    </thead>
                    <tbody className="">
                        {payouts.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="text-center py-6 text-slate-400 text-sm">No payouts found</td>
                            </tr>
                        ) : (
                            sortedPayouts.map((entry, index) => (
                                <React.Fragment key={index}>
                                    <tr className="bg-white dark:bg-slate-800/50 border-b border-blue-50 dark:border-slate-700/50 hover:bg-blue-50/50 dark:hover:bg-slate-700/30 transition-colors">
                                        <td className="px-3 sm:px-4 md:px-6 py-3 text-xs sm:text-sm font-medium text-blue-600 dark:text-slate-300">
                                            {new Date(entry?.createdTime).toLocaleDateString(undefined, { 
                                                year: '2-digit', 
                                                month: 'short', 
                                                day: 'numeric' 
                                            })}
                                        </td>
                                        <td className="px-3 sm:px-4 md:px-6 py-3 text-xs sm:text-sm font-bold text-[#03436f] dark:text-slate-100">
                                            {entry?.amount.toFixed(8)} <span className="text-[10px] opacity-50 uppercase">{coin}</span>
                                        </td>
                                        <td className="px-3 sm:px-4 md:px-6 py-3">
                                            <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                                                {entry?.transactionStatus && (
                                                    <span
                                                        className={`px-2 py-1 text-xs rounded-lg font-semibold cursor-pointer transition-all ${entry?.transactionStatus === 'TRANSFER_COMPLETED'
                                                            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/30 hover:bg-emerald-100'
                                                            : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/30 hover:bg-blue-100'
                                                            }`}
                                                        onClick={() => {
                                                            if (['CREATED', 'ACCUMULATED', 'TRANSFER_COMPLETED'].includes(entry?.transactionStatus)) {
                                                                toggleRewardToggle(index);
                                                            } else {
                                                                setRewardToggle(null);
                                                            }
                                                        }}
                                                    >
                                                        {entry?.transactionStatus.replace('_', ' ')}
                                                    </span>
                                                )}
                                                <button
                                                    onClick={() => toggleTxIdRow(index)}
                                                    className="px-2 py-1 text-xs rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 font-semibold hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                                                >
                                                    TXID
                                                </button>
                                            </div>
                                        </td>
                                    </tr>

                                    {rewardToggle === index && (
                                        <tr className="bg-slate-50/50 dark:bg-slate-900/30">
                                            <td colSpan={3} className="p-3 border-b border-slate-100 dark:border-slate-700 shadow-inner">
                                                <div className="flex flex-col gap-1.5 max-w-md mx-auto">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center mb-1">Associated Rewards</p>
                                                    {entry?.rewards.map((reward, rewardIndex) => (
                                                        <div key={rewardIndex} className="flex justify-between items-center py-1 px-3 bg-white dark:bg-slate-800 rounded-md border border-slate-100 dark:border-slate-700 shadow-sm text-xs">
                                                            <span className="text-slate-500 dark:text-slate-400">{new Date(reward.date).toLocaleDateString(undefined, { 
                                                                month: 'short', 
                                                                day: 'numeric' 
                                                            })}</span>
                                                            <span className="font-semibold text-slate-700 dark:text-slate-200">{toFixedNoRound(reward.amount, 8)} {coin}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {showTxIdIndex === index && (
                                        <tr className="bg-slate-100/30 dark:bg-slate-900/50">
                                            <td colSpan={3} className="px-3 sm:px-4 md:px-6 py-3 text-xs font-mono text-slate-500 dark:text-slate-400 break-all text-center border-b border-slate-100 dark:border-slate-700 shadow-inner">
                                                <span className="opacity-50 mr-1 text-[10px]">TXID:</span>
                                                <span className="select-all text-[10px] sm:text-xs">{entry?.transactionId}</span>
                                            </td>
                                        </tr>
                                    )}

                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default RevenuePayouts;
